import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCalendarWeek, FaUserFriends } from "react-icons/fa";
import {
  formatDayLabel,
  formatNumber,
  formatRelative,
  formatWeekLabel,
} from "../selectors";
import { SignalList } from "./SignalList";
import { CrmPanel } from "./CrmPanel";
import { RecommendationPanel } from "./RecommendationPanel";

/** Signed day-over-day / week-over-week movement, blank when unknown. */
function Delta({ value }) {
  if (value === null || value === undefined) return <span className="ct-delta ct-delta--none">—</span>;
  if (value === 0) return <span className="ct-delta ct-delta--flat">±0</span>;
  return (
    <span className={`ct-delta ct-delta--${value > 0 ? "up" : "down"}`}>
      {value > 0 ? "+" : ""}
      {formatNumber(value)}
    </span>
  );
}

/**
 * The evidence behind one table row: the day-by-day series (with the
 * day-over-day movement that the summary row can only hint at), the same
 * series folded into weeks, and who the activity actually came from.
 */
export function ClientDetail({ client, crmState, recommendationState, onFetchCrm, onGenerate }) {
  const daily = [...(client.daily || [])].reverse();
  const weekly = [...(client.weekly || [])].reverse();
  const topUsers = client.topUsers || [];

  return (
    <div className="ct-detail">
      {/* The smart layer leads: what is wrong and what to do about it come
          before the raw series that justify them. */}
      <div className="ct-detail-grid ct-detail-grid--smart">
        <section className="ct-detail-panel">
          <header className="ct-detail-head">
            <h4>Why this client scores {client.riskScore}</h4>
            <span className="ct-detail-sub">
              Rule-based, computed from the numbers below
            </span>
          </header>
          <SignalList signals={client.signals} />
        </section>

        <div className="ct-detail-column">
          <CrmPanel
            snapshot={client.crm}
            crm={crmState?.result}
            loading={crmState?.loading}
            onRetry={onFetchCrm}
          />
          <RecommendationPanel state={recommendationState} onGenerate={onGenerate} />
        </div>
      </div>

      <div className="ct-detail-grid">
        <section className="ct-detail-panel">
          <header className="ct-detail-head">
            <h4>Day by day</h4>
            <span className="ct-detail-sub">Newest first · change vs the previous day</span>
          </header>
          <div className="ct-detail-scroll">
            <table className="ct-mini-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Users</th>
                  <th>Accesses</th>
                  <th>Δ</th>
                  <th>Manual runs</th>
                  <th>Training</th>
                  <th>Events</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((point) => (
                  <tr
                    key={point.day}
                    className={point.events === 0 ? "ct-mini-row--quiet" : undefined}
                  >
                    <td className="ct-mini-day">{formatDayLabel(point.day)}</td>
                    <td>{formatNumber(point.activeUsers)}</td>
                    <td>{formatNumber(point.accesses)}</td>
                    <td>
                      <Delta value={point.deltaAccesses} />
                    </td>
                    <td>{formatNumber(point.manualRuns)}</td>
                    <td>{formatNumber(point.trainingInstructions)}</td>
                    <td>{formatNumber(point.events)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="ct-detail-column">
          <section className="ct-detail-panel">
            <header className="ct-detail-head">
              <h4>
                <FaCalendarWeek aria-hidden="true" /> Week over week
              </h4>
              <span className="ct-detail-sub">Weeks start Monday (UTC)</span>
            </header>
            <table className="ct-mini-table">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>Users</th>
                  <th>Accesses</th>
                  <th>Δ</th>
                  <th>Manual runs</th>
                </tr>
              </thead>
              <tbody>
                {weekly.map((week) => (
                  <tr key={week.weekStart}>
                    <td className="ct-mini-day">{formatWeekLabel(week)}</td>
                    <td>{formatNumber(week.activeUsers)}</td>
                    <td>{formatNumber(week.accesses)}</td>
                    <td>
                      <Delta value={week.deltaAccesses} />
                    </td>
                    <td>{formatNumber(week.manualRuns)}</td>
                  </tr>
                ))}
                {weekly.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="ct-empty-cell">
                      No activity in this window.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </section>

          <section className="ct-detail-panel">
            <header className="ct-detail-head">
              <h4>
                <FaUserFriends aria-hidden="true" /> Busiest users
              </h4>
              <Link
                className="ct-detail-link"
                to={`/org-usage-analytics/${client.organizationId}`}
              >
                Full org breakdown <FaArrowRight aria-hidden="true" />
              </Link>
            </header>
            <table className="ct-mini-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Accesses</th>
                  <th>Manual runs</th>
                  <th>Events</th>
                  <th>Last active</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((user) => (
                  <tr key={user.userId}>
                    <td className="ct-mini-user" title={user.email || user.userId}>
                      {user.email || user.userId}
                    </td>
                    <td>{formatNumber(user.accesses)}</td>
                    <td>{formatNumber(user.manualRuns)}</td>
                    <td>{formatNumber(user.events)}</td>
                    <td>{formatRelative(user.lastActiveAt)}</td>
                  </tr>
                ))}
                {topUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="ct-empty-cell">
                      Nobody was active in this window.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </div>
  );
}
